import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  PiggyBank, TrendingUp, ShoppingBag, Award,
  Cpu, Speaker, Gamepad2, Smartphone, Watch, Cable, Wrench, Shield
} from 'lucide-react';
import type { DiscountPurchase, MembershipPlan } from './types';

const categoryIcons: Record<string, React.ElementType> = {
  'Tecnología': Cpu,
  'Audio': Speaker,
  'Gaming': Gamepad2,
  'Estuches': Smartphone,
  'Relojes': Watch,
  'Cargadores': Cable,
  'Reparaciones': Wrench,
  'Vidrios': Shield,
};

// Use consistent blue styling for all plans
const cardStyle = {
  gradient: 'from-blue-900/30 to-blue-800/20',
  border: 'border-blue-600/30',
  icon: 'text-blue-500',
  accent: 'text-blue-400',
};

interface SavingsSummaryCardProps {
  purchases: DiscountPurchase[];
  plan: MembershipPlan;
}

export const SavingsSummaryCard: React.FC<SavingsSummaryCardProps> = ({
  purchases,
  plan,
}) => {
  const styles = cardStyle;
  
  const stats = useMemo(() => {
    const now = new Date();
    const currentMonthYear = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    
    const thisMonthPurchases = purchases.filter(p => p.month_year === currentMonthYear);
    const thisMonthSavings = thisMonthPurchases.reduce((sum, p) => sum + p.savings, 0);
    const totalSavings = purchases.reduce((sum, p) => sum + p.savings, 0);
    const totalPurchases = purchases.length;
    
    // Category breakdown with count and savings
    const categoryBreakdown: Record<string, { count: number; savings: number }> = {};
    purchases.forEach(p => {
      if (!categoryBreakdown[p.category]) {
        categoryBreakdown[p.category] = { count: 0, savings: 0 };
      }
      categoryBreakdown[p.category].count += 1;
      categoryBreakdown[p.category].savings += p.savings;
    });
    
    // Sort by savings descending
    const sortedCategories = Object.entries(categoryBreakdown)
      .sort((a, b) => b[1].savings - a[1].savings);
    
    const topCategory = sortedCategories[0];
    
    // Monthly breakdown for last 6 months
    const monthlyBreakdown: { month: string; savings: number }[] = [];
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = date.toLocaleDateString('es-CR', { month: 'short' });
      const monthSavings = purchases
        .filter(p => p.month_year === monthKey)
        .reduce((sum, p) => sum + p.savings, 0);
      monthlyBreakdown.push({ month: monthName, savings: monthSavings });
    }
    
    const maxMonthlySavings = Math.max(...monthlyBreakdown.map(m => m.savings), 1);
    
    return {
      thisMonthSavings,
      totalSavings,
      totalPurchases,
      topCategory: topCategory ? topCategory[0] : null,
      categoryBreakdown: sortedCategories,
      monthlyBreakdown,
      maxMonthlySavings,
    };
  }, [purchases]);

  if (purchases.length === 0) {
    return (
      <div className={`bg-gradient-to-br ${styles.gradient} backdrop-blur-xl rounded-2xl p-5 border ${styles.border}`}>
        <div className="flex items-center gap-2 mb-4">
          <PiggyBank className={`w-5 h-5 ${styles.icon}`} />
          <span className="text-white font-medium">Tus Ahorros</span>
        </div>
        <p className="text-white/50 text-sm text-center py-4">
          Aún no tienes compras con descuento registradas
        </p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-gradient-to-br ${styles.gradient} backdrop-blur-xl rounded-2xl p-5 border ${styles.border}`}
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <PiggyBank className={`w-5 h-5 ${styles.icon}`} />
        <span className="text-white font-medium">Tus Ahorros con Membresía</span>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {/* This Month */}
        <div className="bg-white/5 rounded-xl p-3 border border-white/10">
          <div className="flex items-center gap-1.5 text-white/50 text-xs mb-1">
            <TrendingUp className="w-3 h-3" />
            <span>Este mes</span>
          </div>
          <p className={`text-xl font-bold ${styles.accent}`}>
            ₡{stats.thisMonthSavings.toLocaleString()}
          </p>
        </div>

        {/* Total Savings */}
        <div className="bg-white/5 rounded-xl p-3 border border-white/10">
          <div className="flex items-center gap-1.5 text-white/50 text-xs mb-1">
            <PiggyBank className="w-3 h-3" />
            <span>Total ahorrado</span>
          </div>
          <p className="text-xl font-bold text-green-400">
            ₡{stats.totalSavings.toLocaleString()}
          </p>
        </div>

        {/* Purchase Count */}
        <div className="bg-white/5 rounded-xl p-3 border border-white/10">
          <div className="flex items-center gap-1.5 text-white/50 text-xs mb-1">
            <ShoppingBag className="w-3 h-3" />
            <span>Compras</span>
          </div>
          <p className="text-xl font-bold text-white">
            {stats.totalPurchases}
          </p>
        </div>

        {/* Top Category */}
        <div className="bg-white/5 rounded-xl p-3 border border-white/10">
          <div className="flex items-center gap-1.5 text-white/50 text-xs mb-1">
            <Award className="w-3 h-3" />
            <span>Top categoría</span>
          </div>
          <p className="text-sm font-bold text-white truncate">
            {stats.topCategory || '-'}
          </p>
        </div>
      </div>

      {/* Monthly Chart */}
      <div className="bg-white/5 rounded-xl p-3 border border-white/10">
        <p className="text-white/50 text-xs mb-3">Últimos 6 meses</p>
        <div className="flex items-end gap-1 h-16">
          {stats.monthlyBreakdown.map((month, i) => {
            const height = (month.savings / stats.maxMonthlySavings) * 100;
            const isCurrentMonth = i === stats.monthlyBreakdown.length - 1;
            
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: `${Math.max(height, 4)}%` }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                  className={`w-full rounded-t ${
                    isCurrentMonth 
                      ? 'bg-gradient-to-t from-green-500 to-green-400' 
                      : 'bg-white/20'
                  }`}
                />
                <span className="text-[10px] text-white/40 capitalize">{month.month}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Category Breakdown */}
      {stats.categoryBreakdown.length > 0 && (
        <div className="bg-white/5 rounded-xl p-3 border border-white/10 mt-3">
          <p className="text-white/50 text-xs mb-3">Categorías compradas</p>
          <div className="space-y-2">
            {stats.categoryBreakdown.map(([category, data], i) => {
              const CategoryIcon = categoryIcons[category] || ShoppingBag;
              const percentage = Math.round((data.savings / stats.totalSavings) * 100);
              
              return (
                <motion.div
                  key={category}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-center gap-2"
                >
                  <div className="w-7 h-7 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                    <CategoryIcon className="w-3.5 h-3.5 text-purple-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white text-xs font-medium truncate">{category}</span>
                      <span className="text-green-400 text-xs font-semibold">₡{data.savings.toLocaleString()}</span>
                    </div>
                    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${percentage}%` }}
                        transition={{ delay: i * 0.05 + 0.2, duration: 0.5 }}
                        className="h-full bg-gradient-to-r from-purple-500 to-purple-400 rounded-full"
                      />
                    </div>
                  </div>
                  <span className="text-white/40 text-[10px] w-8 text-right">{data.count}x</span>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default SavingsSummaryCard;
